import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from "antd";

import { useModal } from "@/hooks/modals/useModal";
import { PlusOutlined } from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useCancelReasonBehavior } from "../hooks/useCancelReasonBehavior";
import { useFormEditable } from "../hooks/useFormEditable";
import { useSourcingFollowBehavior } from "../hooks/useSourcingFollowBehavior";
import type {
  MaterialItem,
  SourcingRequirementApplicationFormFields,
} from "../type";
import { CancelReasonModal } from "./CancelReasonModal";
import styles from "./MaterialFormFields.module.less";
import { SourcingFollowModal } from "./sourcingNodeFollow/SouringFollowModal";
import { defaultNonProductionMaterialSourcingNodes } from "./sourcingNodeFollow/node.config";
import { useSourcingRequirementQuery } from "../hooks/useSourcingRequirementQuery";
import { sourcingMethodOptions } from "@/constants";
import { useTranslation } from "react-i18next";

export function NonProductionMaterialFormFields() {
  const { t } = useTranslation("sourcingRequirementManagement");
  const { t: tCommon } = useTranslation("common");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { isMainFormDisabled, isSourcingByFormDisabled } = useFormEditable();

  const { props } = useModal("SOURCING_APPLICATION_20251202");
  const { mode, recordId } = props;
  const isStarted = mode === "started";
  const detail = useSourcingRequirementQuery(recordId);

  // 申请寻源已完成
  const isCompleted = detail?.reviewStatus === 4;

  const form = Form.useFormInstance<SourcingRequirementApplicationFormFields>();

  const {
    cancelReasonModalOpen,
    sourcingMaterialId,
    handleCancelModalCancel,
    handleCancelModalConfirm,
    handleCancelSourcingChange,
  } = useCancelReasonBehavior(form);

  const {
    sourcingFollowModalOpen,
    currentSourcingMaterialId,
    handleViewSourcingProgress,
    handleCloseSourcingFollowModal,
  } = useSourcingFollowBehavior();

  const openViewSourcingProgress = (fieldIndex: number) => {
    const sourcingMaterialId = form.getFieldValue([
      "materialList",
      fieldIndex,
      "id",
    ]);
    handleViewSourcingProgress(sourcingMaterialId);
  };

  return (
    <Form.List name="materialList">
      {(fields, { add, remove }) => {
        const columns = createColumns({
          hoverIndex,
          isMainFormDisabled,
          remove,
          isCompleted,
          openViewSourcingProgress,
          form,
          isStarted,
          handleCancelSourcingChange,
          isSourcingByFormDisabled,
          t,
        });

        return (
          <>
            <Table
              bordered
              dataSource={fields}
              pagination={false}
              scroll={{ x: 1200 }}
              onRow={(_, index) => ({
                onMouseEnter: () => setHoverIndex(index!),
                onMouseLeave: () => setHoverIndex(null),
              })}
              columns={columns}
            ></Table>

            <Button
              icon={<PlusOutlined />}
              hidden={isMainFormDisabled}
              onClick={add}
              variant="outlined"
              color="primary"
              className={styles.addBtn}
            >
              {tCommon("新增")}
            </Button>

            <CancelReasonModal
              sourcingMaterialId={sourcingMaterialId}
              open={cancelReasonModalOpen}
              onClose={handleCancelModalCancel}
              onOk={handleCancelModalConfirm}
            />

            <SourcingFollowModal
              currentSourcingMaterialId={currentSourcingMaterialId!}
              defaultSourcingNodes={defaultNonProductionMaterialSourcingNodes}
              open={sourcingFollowModalOpen}
              onClose={handleCloseSourcingFollowModal}
            />
          </>
        );
      }}
    </Form.List>
  );
}

type CreateColumnsFn = (params: {
  hoverIndex: number | null;
  isMainFormDisabled: boolean;
  remove: (index: number) => void;
  isCompleted: boolean;
  openViewSourcingProgress: (fieldIndex: number) => void;
  form: FormInstance<SourcingRequirementApplicationFormFields>;
  isStarted: boolean;
  handleCancelSourcingChange: (
    isChecked: boolean,
    index: number,
    sourcingMaterialId: number,
  ) => void;
  isSourcingByFormDisabled: boolean;
  t: (key: string) => string;
}) => TableColumnsType;

const createColumns: CreateColumnsFn = (params) => {
  const { isCompleted, form } = params;
  let columns: TableColumnsType = initColumns(params);

  // 受原型字段顺序影响, 不能和后面 isCompleted 同时添加
  if (isCompleted) {
    columns = [...columns, ...addLastColumns(params)];
  }

  const materialList = form.getFieldValue("materialList");

  const showCancelReason = materialList.some(
    (m: MaterialItem) => m.isSourcingCancel && !!m.cancelReason,
  );

  columns = [...columns, ...addCancelSourcingColumn(params)];

  if (showCancelReason) {
    columns = [...columns, ...addCancelReasonColumn(params)];
  }

  if (isCompleted) {
    columns = [...columns, ...addSoucingExecutionColumn(params)];
  }

  return columns;
};

// 初始化列
const initColumns: CreateColumnsFn = ({
  hoverIndex,
  isMainFormDisabled,
  remove,
  t,
}) => {
  const columns: TableColumnsType<any> = [
    {
      title: t("序号"),
      dataIndex: "materialCode",
      width: 80,
      align: "center",
      fixed: "left",
      render: (_, field, index) => {
        const isHover = hoverIndex === index;
        const canRemove = isHover && !isMainFormDisabled;
        return canRemove ? (
          <div className={styles.removeIcon} onClick={() => remove(index)}>
            <Trash2 size={20} />
          </div>
        ) : (
          index + 1
        );
      },
    },

    {
      title: t("寻源名称"),
      width: 120,
      dataIndex: "materialName",
      align: "center",
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "materialName"]}
          rules={[
            {
              required: true,
              message: t("请输入物料名称"),
            },
          ]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: t("需求数量"),
      width: 120,
      dataIndex: "count",
      align: "center",
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "count"]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },

    {
      title: t("要求完成日期"),
      dataIndex: "completeDate",
      align: "center",
      width: 200,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "completeDate"]}
          rules={[
            {
              required: true,
              message: t("要求完成日期"),
            },
          ]}
          noStyle
        >
          <DatePicker />
        </Form.Item>
      ),
    },

    {
      title: t("寻源原因"),
      width: 150,
      dataIndex: "sourcingReason",
      align: "center",
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          noStyle
          name={[field.name, "sourcingReason"]}
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: t("寻源要求"),
      width: 150,
      dataIndex: "sourcingRequirements",
      align: "center",
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "sourcingRequirements"]}
          noStyle
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: t("寻源优先级"),
      dataIndex: "sourcingPrioritize",
      align: "center",
      width: 180,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "sourcingPrioritize"]}
          noStyle
        >
          <Radio.Group
            options={[
              { value: 1, label: t("紧急") },
              { value: 2, label: t("一般") },
            ]}
          />
        </Form.Item>
      ),
    },
  ];

  return columns;
};

// 取消寻源列
const addCancelSourcingColumn: CreateColumnsFn = ({
  isCompleted,
  isStarted,
  handleCancelSourcingChange,
  form,
  t,
}) => {
  const columns: TableColumnsType = [
    {
      title: t("取消寻源"),
      dataIndex: "isSourcingCancel",
      align: "center",
      width: 100,
      render: (_, field, index) => {
        /**
         * 同时满足以下条件时，允许编辑取消寻源复选框
         * 1. 当前审核状态为“完成”（reviewStatus === "4"）
         * 2. mode 为 started
         * 3. 该物料的取消寻源状态尚未设置
         */
        const isSourcingCancel = form.getFieldValue([
          "materialList",
          field.name,
          "isSourcingCancel",
        ]);
        const isEditable = isCompleted && isStarted && !isSourcingCancel;

        return (
          <>
            <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
              name={[field.name, "isSourcingCancel"]}
              noStyle
              valuePropName="checked"
            >
              <Checkbox
                onChange={(e) =>
                  handleCancelSourcingChange(
                    e.target.checked,
                    index,
                    form.getFieldValue(["materialList", field.name, "id"]),
                  )
                }
                disabled={!isEditable}
              ></Checkbox>
            </Form.Item>

            <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
              name={[field.name, "id"]}
              hidden
            ></Form.Item>
          </>
        );
      },
    },
  ];
  return columns;
};

// 添加"寻源执行"列
const addSoucingExecutionColumn: CreateColumnsFn = ({
  openViewSourcingProgress,
  t,
}) => {
  const columns: TableColumnsType = [
    {
      title: t("寻源执行"),
      dataIndex: "soucingExecution",
      align: "center",
      fixed: "right",
      width: 150,
      render: (_, field) => {
        return (
          <Button
            disabled={false}
            onClick={() => openViewSourcingProgress(field.name)}
            variant="link"
            color="primary"
          >
            {t("查看寻源进度")}
          </Button>
        );
      },
    },
  ];

  return columns;
};

// 寻源申请完成后续要添加的列
const addLastColumns: CreateColumnsFn = ({ isSourcingByFormDisabled, t }) => {
  const columns: TableColumnsType = [
    {
      title: t("寻源方法"),
      dataIndex: "sourcingMethod",
      align: "center",
      width: 140,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "sourcingMethod"]}
            noStyle
          >
            <Select
              style={{ width: "100%" }}
              placeholder={t("请选择")}
              disabled={isSourcingByFormDisabled}
              options={sourcingMethodOptions}
            ></Select>
          </Form.Item>
        );
      },
    },
    {
      title: t("计划开始时间"),
      dataIndex: "planStartTime",
      align: "center",
      width: 200,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "planStartTime"]}
          noStyle
        >
          <DatePicker disabled={isSourcingByFormDisabled} />
        </Form.Item>
      ),
    },
    {
      title: t("计划结束时间"),
      dataIndex: "planEndTime",
      align: "center",
      width: 200,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "planEndTime"]}
          noStyle
        >
          <DatePicker disabled={isSourcingByFormDisabled} />
        </Form.Item>
      ),
    },

    {
      title: t("单价"),
      dataIndex: "unitPrice",
      align: "center",
      width: 120,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "unitPrice"]}
            noStyle
          >
            <InputNumber
              min={0}
              precision={2}
              placeholder={t("请输入")}
              disabled={isSourcingByFormDisabled}
            />
          </Form.Item>
        );
      },
    },
    {
      title: t("总金额"),
      dataIndex: "totalPrice",
      align: "center",
      width: 120,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "totalPrice"]}
            noStyle
          >
            <InputNumber
              min={0}
              precision={2}
              placeholder={t("请输入")}
              disabled={isSourcingByFormDisabled}
            />
          </Form.Item>
        );
      },
    },
  ];

  return columns;
};

// 添加"取消原因"列
const addCancelReasonColumn: CreateColumnsFn = ({ t }) => {
  const columns: TableColumnsType = [
    {
      title: t("取消原因"),
      dataIndex: "cancelReason",
      align: "center",
      width: 140,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            noStyle
            name={[field.name, "cancelReason"]}
          >
            <Input.TextArea rows={1} placeholder={t("请输入")} disabled />
          </Form.Item>
        );
      },
    },
  ];
  return columns;
};
